/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/ivrApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for IVRs.
    Centralizes all API calls related to IVRs.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  IVR,
  IVRPayload,
  ListIVRsResponse,
  IVRResponse,
} from '../types';

export const IVRS_ENDPOINT = '/api/telephony/ivrs';

/**
 * Generic API request helper
 */
async function requestIVRsApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit,
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List IVRs with pagination and search
 */
export async function fetchIVRs(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string,
): Promise<ListIVRsResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder,
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${IVRS_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch IVRs');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as IVR[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0,
    },
  };
}

/**
 * Get single IVR by ID
 */
export async function fetchIVR(id: string | number): Promise<IVR> {
  const { data } = await requestIVRsApi<IVR>(
    `${IVRS_ENDPOINT}/${id}`,
    'Failed to fetch IVR',
  );
  return data;
}

/**
 * Create new IVR
 */
export async function createIVR(payload: IVRPayload): Promise<IVR> {
  const { data } = await requestIVRsApi<IVR>(
    IVRS_ENDPOINT,
    'Failed to create IVR',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Update IVR
 */
export async function updateIVR(
  id: string | number,
  payload: IVRPayload,
): Promise<IVR> {
  const { data } = await requestIVRsApi<IVR>(
    `${IVRS_ENDPOINT}/${id}`,
    'Failed to update IVR',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Delete IVR
 */
export async function deleteIVR(id: string | number): Promise<void> {
  await requestIVRsApi<null>(
    `${IVRS_ENDPOINT}/${id}`,
    'Failed to delete IVR',
    {
      method: 'DELETE',
    },
  );
}

